import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { ConflictsNav, Expanded, ExpandedTable } from "@ryde/controls";
import {
  buildSkippedIdTranslationParams,
  ConflictErrorSeverity,
  localizePermissionTranslationParams,
  PLURAL_KEY,
  PROCESS_SKIPPED_IDS_DEFAULT_COLLAPSED_FROM,
  type IGroupedProcessError,
  type IRydeProcess,
} from "@ryde/shared";

import { ProcessDrawerContext } from "../process-drawer-context";
import {
  PROCESS_WARNING_ACCENT,
  ProcessErrorsTableWrapStyled,
} from "../process.styled";
import { ProcessEntityIdChips } from "./process-entity-id-chips";
import { ProcessErrorsArchiveRelation } from "./process-errors-archive-relation";

export const ProcessErrorsGeneric: FC<{
  kind: "skipped" | "warnings";
  groupedErrors: IGroupedProcessError[];
}> = ({ kind, groupedErrors }) => {
  const { t } = useTranslation();
  const { currentProcess, entity } = useContext(ProcessDrawerContext);
  const isWarnings = kind === "warnings";
  const sectionCount = groupedErrors.reduce(
    (count, group) => count + (group.rideIds.length || 1),
    0,
  );

  if (!groupedErrors.length) {
    return null;
  }

  return (
    <Expanded
      defaultExpanded={
        sectionCount < PROCESS_SKIPPED_IDS_DEFAULT_COLLAPSED_FROM
      }
      NavControl={
        <ConflictsNav
          title={t(isWarnings ? "process.warnings" : "process.skipped")}
          conflictNumber={sectionCount}
          severity={
            isWarnings
              ? ConflictErrorSeverity.Warning
              : ConflictErrorSeverity.Critical
          }
          accentColor={isWarnings ? PROCESS_WARNING_ACCENT : undefined}
        />
      }
    >
      <ExpandedTable>
        <ProcessErrorsTableWrapStyled>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("process.skippedReason")}</TableCell>
                <TableCell>
                  {entity
                    ? t(`${entity}.${PLURAL_KEY}`)
                    : t("process.skippedRides")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedErrors.map(group => (
                <TableRow
                  key={`${kind}-${group.translationKey}-${group.translationParams?.action ?? ""}-${group.translationParams?.entity ?? ""}`}
                >
                  <TableCell>
                    {t(group.translationKey, {
                      ...buildSkippedIdTranslationParams(
                        group.rideIds.join(", "),
                      ),
                      ...localizePermissionTranslationParams(
                        group.translationKey,
                        group.translationParams,
                        t,
                      ),
                    })}
                  </TableCell>
                  <TableCell>
                    {group.rideIds.length && entity ? (
                      <ProcessEntityIdChips
                        entity={entity}
                        ids={group.rideIds}
                        tone={isWarnings ? "warning" : "skipped"}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ProcessErrorsTableWrapStyled>

        {!isWarnings ? (
          <ProcessErrorsArchiveRelation {...(currentProcess as IRydeProcess)} />
        ) : null}
      </ExpandedTable>
    </Expanded>
  );
};
